import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AutoComplete, Flex, Typography } from 'antd';
import { Moment } from 'moment';
import uuid, { SUUID } from 'short-uuid';
import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation } from 'react-query';
import isEqual from 'lodash.isequal';
import { filterUnique } from '@shared/utils/array';
import { toMoment } from '@shared/utils/date';
import { calculateTotal } from '@shared/utils/math';
import { formatNumber } from '@shared/utils/formatNumber';
import { CargoContractSides } from '@shared/utils/constants';
import { CargoWithPortsAndCalculationsJSON } from '@api/features/laytime/getCargosAndLaytimes';
import { SplitRow } from '@api/features/cargos/splitCargo';
import EditableCellTableRedux from '@client/components/EditableTableRedux/EditableCellTableRedux';
import AddButton from '@client/components/AddButton';
import Button from '@client/components/Button';
import { useSaveButton } from '@client/utils/hooks/useSaveButton';
import { getUserData, splitCargo } from '@client/lib/api';
import useFetchedState from '@client/utils/hooks/useFetchedState';
import getRelevantCargoTypes from '@client/screens/cargos/details/helpers/getRelevantCargoTypes';
import { useVoyage } from '../../components/VoyageProvider/VoyageProvider';
import styles from './CargoSplitModal.module.css';

const getEmptyRow = () => (
	{
		id: uuid.generate(),
		cargoText: null,
		quantity: null,
		date: null,
		blNumber: null,
		description: null,
	});

const getSavedRows = (
	cargo: CargoWithPortsAndCalculationsJSON,
	cargoPortId: number,
): Array<Row> => (
	cargo.CargoPorts
		.find((cp) => cp.id === cargoPortId)
		?.CargoSplitItems ?? []
).map((c) => ({
	id: c.id,
	cargoText: c.cargoText,
	quantity: c.quantity,
	date: c.date,
	blNumber: c.blNumber,
	description: c.description,
}));

const getRows = (
	cargo: CargoWithPortsAndCalculationsJSON,
	cargoPortId: number,
) => {
	const savedRows = getSavedRows(cargo, cargoPortId);

	return [
		{
			id: -1,
			cargoText: cargo.type,
			quantity: calculateTotal(savedRows, (r) => r.quantity ?? 0),
			date: null,
			blNumber: null,
			description: null,
		},
		...savedRows,
	];
};

const getInitialRows = (
	cargo: CargoWithPortsAndCalculationsJSON,
	cargoPortId: number,
) => {
	const rows = getRows(cargo, cargoPortId);

	if (rows.length > 1) {
		return rows;
	}

	return [...rows, getEmptyRow(), getEmptyRow()];
};

type Row = {
	id: number | SUUID;
	cargoText: string | null;
	quantity: number | null;
	date: Moment | null;
	blNumber: string | null;
	description: string | null;
};

const normalizeText = (value: string | null | undefined) => {
	const trimmed = value?.trim();

	return trimmed == null || trimmed === '' ? null : trimmed;
};

const normalizeQuantity = (value: number | null | undefined) => (
	value == null || Number.isNaN(value) ? null : value
);

const normalizeDate = (value: Moment | Date | string | null | undefined) => {
	if (value == null || value === '') {
		return null;
	}

	const parsed = toMoment(value);

	return parsed.isValid() ? parsed.valueOf() : null;
};

const getComparableValues = (row: Row) => [
	normalizeText(row.cargoText),
	normalizeQuantity(row.quantity),
	normalizeDate(row.date),
	normalizeText(row.blNumber),
	normalizeText(row.description),
];

const isFilledRow = (row: Row) => (
	row.id !== -1 && getComparableValues(row).some((value) => value != null)
);

const toComparableRows = (rows: Array<Row>) => rows
	.filter(isFilledRow)
	.map(getComparableValues)
	.map((values) => JSON.stringify(values))
	.sort();

type Props = {
	cargo: CargoWithPortsAndCalculationsJSON;
	cargoPortId: number;
};

const CargoSplitModal = ({
	cargo,
	cargoPortId,
}: Props) => {
	const [rows, setRows] = useState<Array<Row>>(() => getInitialRows(cargo, cargoPortId));
	const disabled = cargo.contractSide === CargoContractSides.VC_IN;

	const {
		estimateDetails,
		refreshCargos,
	} = useVoyage();

	const [userData] = useFetchedState(getUserData);

	const cargoOptions = useMemo(() => filterUnique(
		getRelevantCargoTypes(userData, estimateDetails ?? null),
		(cargoType) => cargoType.name,
	).map((cargoType) => ({
		label: cargoType.name,
		value: cargoType.name,
	})), [userData, estimateDetails]);

	const savedRows = useMemo(
		() => getSavedRows(cargo, cargoPortId),
		[cargo, cargoPortId],
	);

	const savedRowsKey = JSON.stringify(
		savedRows.map((row) => [row.id, ...getComparableValues(row)]),
	);

	const hasChanges = useMemo(() => !isEqual(
		toComparableRows(savedRows),
		toComparableRows(rows),
	), [rows, savedRows]);

	const seededKey = useRef(savedRowsKey);

	useEffect(() => {
		if (seededKey.current === savedRowsKey) {
			return;
		}

		seededKey.current = savedRowsKey;
		setRows(getInitialRows(cargo, cargoPortId));
	}, [cargo, cargoPortId, savedRowsKey]);

	const saveMutation = useMutation({
		mutationFn: ({ cargoId, rows }: { cargoId: number; rows: Array<SplitRow> }) => splitCargo({
			rows: rows as Array<SplitRow>,
			cargoPortId,
			cargoId: cargoId,
		}),
		onSuccess: () => refreshCargos(),
	});

	const missingValues = useMemo(() => {
		return rows.some((row) => row.cargoText == null || row.cargoText === '' || row.quantity == null || row.quantity === 0);
	}, [rows]);

	const saveBtn = useSaveButton({
		onSave: () => {
			if (!cargo.id || !cargoPortId) {
				return;
			}

			saveMutation.mutate({
				cargoId: cargo.id,
				rows: rows.filter(isFilledRow) as Array<SplitRow>,
			});
		},
		hasPendingChanges: hasChanges,
		loading: saveMutation.isLoading,
		disabled: missingValues,
		disabledTooltip: missingValues ? 'One or more rows are missing values in the BL Cargo or BL Quantity fields' : undefined,
	});

	const onChange = (rows: Array<Row>) => {
		const summaryRow = rows.find((row) => row.id === -1);
		const otherRows = rows.filter((row) => row.id !== -1);

		if (summaryRow) {
			summaryRow.quantity = calculateTotal(otherRows, (r) => r.quantity ?? 0);
		}

		setRows(rows);
	};

	const onAddRow = () => {
		setRows((rows) => ([...rows, getEmptyRow()]));
	};

	const onDeleteRow = (id: number | SUUID) => {
		setRows((rows) => rows.filter((row) => row.id !== id));
	};

	return (
		<Flex vertical gap={10}>
			<EditableCellTableRedux
				className={styles.table}
				title={() => (
					<Flex gap={10}>
						<Typography.Title style={{ margin: 0 }} level={3}>Split cargo</Typography.Title>
						{!disabled && (
							<AddButton
								onClick={onAddRow}
							/>
						)}
					</Flex>
				)}
				size="small"
				dataSource={rows}
				rowClassName={(row) => {
					if (row.id === -1) {
						return styles.disabledRow;
					}
				}}
				bordered
				onChange={onChange}
				columns={[
					{
						editable: false,
						type: 'text',
						title: '',
						dataIndex: 'id',
						renderValue: ({ row }) => {
							if (row.id === -1) {
								return 'Total';
							}

							const idx = rows.findIndex((r) => r.id === row.id);

							if (idx < 0) {
								return '';
							}

							return `BL ${idx}`;
						}
					},
					{
						editable: (row) => row.id !== -1 && !disabled,
						type: 'text',
						title: 'BL Cargo',
						dataIndex: 'cargoText',
						inputProps: {
							required: true,
							placeholder: 'Cargo'
						},
						width: 175,
						renderInput: ({ value, onChange, inputProps }) => (
							<AutoComplete
								className={styles.fullWidth}
								variant="borderless"
								placeholder={inputProps?.placeholder}
								value={value ?? undefined}
								options={cargoOptions}
								popupMatchSelectWidth={false}
								filterOption={(inputValue, option) => (
									option?.label?.toUpperCase().includes(inputValue.toUpperCase()) ?? false
								)}
								onChange={onChange}
							/>
						),
					},
					{
						editable: (row) => row.id !== -1 && !disabled,
						type: 'number',
						title: `BL Quantity (${cargo.unit})`,
						dataIndex: 'quantity',
						align: 'right',
						className: styles.alignRight,
						inputProps: {
							placeholder: 'Quantity',
							required: true,
						},
						width: 175,
						renderValue: ({ row }) => row && row.quantity ?
							formatNumber(row.quantity) :
							undefined,
					},
					{
						editable: (row) => row.id !== -1 && !disabled,
						type: 'date',
						title: 'BL Date',
						dataIndex: 'date',
						inputProps: {
							placeholder: 'Date'
						},
						width: 125,
					},
					{
						editable: (row) => row.id !== -1 && !disabled,
						type: 'text',
						title: 'BL Number',
						dataIndex: 'blNumber',
						inputProps: {
							placeholder: 'Number'
						}
					},
					{
						editable: (row) => row.id !== -1 && !disabled,
						type: 'text',
						title: 'BL Description',
						dataIndex: 'description',
						inputProps: {
							placeholder: 'Description'
						}
					},
					{
						title: '',
						render: (row: Row) => row.id !== -1 && !disabled && (
							<Button
								danger
								type="text"
								icon={(<FontAwesomeIcon icon={faTrash} />)}
								onClick={() => onDeleteRow(row.id)} />
						)
					}
				]}
				pagination={false}
			/>
			<div>
				{!disabled && saveBtn}
			</div>
		</Flex>
	);
};

export default CargoSplitModal;
